import { Controller } from "@hotwired/stimulus";
export default class extends Controller {
  static targets = ["input", "suggest", "list"];

  connect() {
    this.debounceDelay = 300;
    this.debounceTimer = null;
    this.currentQuery = "";
    this.selectedIndex = -1;
    this.isComposing = false;
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  onInput() {
    const query = this.inputTarget.value.trim();

    if (query === this.currentQuery) {
      return;
    }

    this.currentQuery = query;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (query === "" || query.length < 2) {
      this.hideSuggest();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.searchCompanies(query);
    }, this.debounceDelay);
  }

  onCompositionStart() {
    this.isComposing = true;
  }

  onCompositionEnd() {
    this.isComposing = false;
  }

  onInputKeydown(event) {
    const buttons = this.listTarget.querySelectorAll(
      ".companies-search-card__name-suggest-button"
    );
    const isVisible = this.suggestTarget.classList.contains("is-opend");

    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Tab"
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (isVisible && buttons.length > 0) {
      switch (event.key) {
        case "ArrowDown":
          this.selectedIndex = 0;
          buttons[this.selectedIndex].focus();
          break;
        case "Tab":
          if (!event.shiftKey) {
            this.selectedIndex = 0;
            buttons[this.selectedIndex].focus();
          }
          break;
        case "Escape":
          this.hideSuggest();
          this.selectedIndex = -1;
          break;
      }
    } else {
      if (event.key === "Escape") {
        this.hideSuggest();
        this.selectedIndex = -1;
      }
    }
  }

  onSuggestionKeydown(event) {
    const buttons = this.listTarget.querySelectorAll(
      ".companies-search-card__name-suggest-button"
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          buttons.length - 1
        );
        buttons[this.selectedIndex].focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        if (this.selectedIndex <= 0) {
          this.selectedIndex = -1;
          this.inputTarget.focus();
        } else {
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
          buttons[this.selectedIndex].focus();
        }
        break;
      case "Tab":
        // Tabで最後の候補から出る場合、サジェストを閉じる
        if (!event.shiftKey && this.selectedIndex >= buttons.length - 1) {
          this.hideSuggest();
          this.selectedIndex = -1;
          // デフォルトのTab動作を許可（次の要素にフォーカス）
        }
        // Shift+Tabで最初の候補から出る場合、サジェストを閉じる
        else if (event.shiftKey && this.selectedIndex <= 0) {
          this.hideSuggest();
          this.selectedIndex = -1;
          // デフォルトのTab動作を許可（前の要素にフォーカス）
        }
        break;
      case "Enter":
        event.preventDefault();
        const selectedButton = buttons[this.selectedIndex];
        const companyName = selectedButton.querySelector(
          ".companies-search-card__name-suggest-text"
        ).textContent;
        this.selectCompany(companyName);
        break;
      case "Escape":
        this.hideSuggest();
        this.selectedIndex = -1;
        this.inputTarget.focus();
        break;
    }
  }

  async searchCompanies(query) {
    try {
      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(`/companies/names?q=${encodedQuery}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const companies = await response.json();
      this.displaySuggest(companies);
    } catch (error) {
      console.error("会社名検索エラー:", error);
      this.hideSuggest();
    }
  }

  displaySuggest(companies) {
    if (!companies || companies.length === 0) {
      this.hideSuggest();
      return;
    }

    this.listTarget.innerHTML = "";

    companies.forEach((company, index) => {
      const li = document.createElement("li");
      li.className = "companies-search-card__name-suggest-item";

      const button = document.createElement("button");
      button.type = "button";
      button.className = "companies-search-card__name-suggest-button";

      const span = document.createElement("span");
      span.className = "companies-search-card__name-suggest-text";
      span.textContent = company;
      button.appendChild(span);
      button.addEventListener("click", () => {
        this.selectCompany(company);
      });

      button.addEventListener("focus", () => {
        this.selectedIndex = index;
      });

      button.addEventListener("keydown", (event) => {
        this.onSuggestionKeydown(event);
      });

      li.appendChild(button);
      this.listTarget.appendChild(li);
    });

    this.selectedIndex = -1;
    this.showSuggest();
  }

  selectCompany(companyName) {
    this.inputTarget.value = companyName;
    this.currentQuery = companyName;
    this.hideSuggest();
    this.inputTarget.focus();
  }

  showSuggest() {
    this.suggestTarget.classList.add("is-opend");
  }

  hideSuggest() {
    this.suggestTarget.classList.remove("is-opend");
    this.selectedIndex = -1;
  }

  onInputClick() {
    const query = this.inputTarget.value.trim();
    if (query !== "" && query.length >= 2) {
      this.searchCompanies(query);
    }
  }

  onClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggest();
    }
  }
}
